import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Form, Field } from 'react-final-form';
import { Link } from "react-router-dom";
import { Row, Col, ButtonGroup, Button, Card, CardHeader, CardBody, CardTitle, CardText, CardFooter } from 'reactstrap';
import { FormGroup, Label } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { loadStudent, loadGroups, loadUsers, loadStudents, removeStudentParent } from '../../../redux/modules';
import { Student, StudentEnrolment, Payment, User } from '../../../models/';
import { Utils, isSuperAdmin, hasModule } from '../../../services/';
import {
    ParentsField, ParentListItem,
    GroupsField, ClassroomListItem,
    StudentsField, StudentListItem,
    InvoiceDetails,
} from '../../../components';
import Icon from '@mdi/react';
import {
    mdiLoading,
    mdiAccountArrowRightOutline,
    mdiAccountCircleOutline,
    mdiAccountChildOutline,
    mdiCardAccountPhoneOutline,
    mdiAccountGroupOutline,
    mdiAccountMultiplePlus,
    mdiCommentAccountOutline,
    mdiAccountCreditCardOutline,
    mdiTrashCanOutline,
} from '@mdi/js';

import { DatePicker } from 'react-widgets';

import './StudentForm.css';

const StudentForm = ({ onSubmit, isCreation }) => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const { users }              = useSelector(state => state.users);
    const { groups }             = useSelector(state => state.groups);
    const { student, students, isLoading, isLoadingOne } = useSelector(state => state.students);

    const renderDateTimePicker = ({ input: { onChange, value }, showTime }) =>
        <DatePicker
            onChange={onChange}
            format="DD MMM YYYY"
            includeTime={showTime}
            value={!value ? null : new Date(value)}
        />

    useEffect(() => {
        dispatch(loadStudents());
        dispatch(loadUsers());
        dispatch(loadGroups());
    }, []);

    const reloadStudent = () => {
        dispatch(loadStudent(student.id));
    };

    const removeParent = parent => {
        dispatch(removeStudentParent(student, parent));
    };

    return (
        <Form
            initialValues={ student }
            onSubmit={ values => { onSubmit(values); }}
            render={({ handleSubmit, pristine, form, submitting }) => (
                <form className="form" onSubmit={handleSubmit}>
                    <Field component="input" type="hidden" name="user_id" />
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountCircleOutline} size={1.5} />&nbsp;
                                        {t('students.profile')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <FormGroup row>
                                            <Label for="firstname" sm={3}>{t('students.firstname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="firstname" id="firstname" autoComplete="given-name" placeholder={t('students.firstname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="lastname" sm={3}>{t('students.lastname')}</Label>
                                            <Col sm={9}>
                                                <Field required component="input" className="form-control" type="text" name="lastname" id="lastname" autoComplete="family-name" placeholder={t('students.lastname_placeholder')} />
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="gender" sm={3}>{t('students.gender')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="gender" id="gender">
                                                    {
                                                        User.genders().map(g => <option key={g} value={g}>{g}</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        <FormGroup row>
                                            <Label for="birthdate" sm={3}>{t('students.birthdate')}</Label>
                                            <Col sm={9}>
                                                <Field showTime={false} component={renderDateTimePicker} className="form-control" type="text" name="birthdate" id="birthdate" autoComplete="" />
                                            </Col>
                                        </FormGroup>
                                        { !isCreation && (
                                        <FormGroup row>
                                            <Label for="enrolment.status" sm={3}>{t('students.enrolment_status')}</Label>
                                            <Col sm={9}>
                                                <Field required component="select" className="form-control" type="text" name="enrolment.status" id="enrolment.status">
                                                    {
                                                        StudentEnrolment.status().map(s => <option key={s} value={s}>{ t(`student_enrolments.status_${s}`) }</option>)
                                                    }
                                                </Field>
                                            </Col>
                                        </FormGroup>
                                        )}
                                        <FormGroup row>
                                            <Label for="remarks" sm={3}>{t('students.remarks')}</Label>
                                            <Col sm={9}>
                                                <Field component="textarea" rows={ 6 } className="form-control" name="remarks" id="remarks" />
                                            </Col>
                                        </FormGroup>
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        { !isCreation && (
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountChildOutline} size={1.5} />
                                        {t('students.family')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Card color="primary" className="text-light">
                                        <CardBody>
                                            <Row className="mb-0">
                                                <Col className="col-auto">
                                                    <CardTitle tag="h4">
                                                        { t('students.add_members') }
                                                    </CardTitle>
                                                </Col>
                                                <Col>
                                                    <CardText>
                                                        <Row>
                                                            <Col>
                                                                <ParentsField name="new_parents[]" placeholder={t('students.parents')}parents={ users } className="w-100" />
                                                            </Col>
                                                            <Col>
                                                                <StudentsField name="new_siblings[]" placeholder={t('students.siblings')} students={ students } className="w-100" />
                                                            </Col>
                                                        </Row>
                                                    </CardText>
                                                </Col>
                                                <Col className="col-auto">
                                                    <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                                        <span>
                                                            <Icon path={isLoading ? mdiLoading : mdiAccountMultiplePlus} spin={isLoading ? 1 : 0} size={1} />&nbsp;
                                                            {t("common.add")}
                                                        </span>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                    {student?.parents?.length > 0 && (
                                    <CardText tag="div" className="mt-4">
                                        <CardTitle tag="h4">
                                            { t('students.parents') }
                                        </CardTitle>
                                        <Row className="row-cols-sm-1 row-cols-lg-1 row-cols-xl-1">
                                        { student?.parents?.map(parent => (
                                            <Col sm="12" md="12" className="mb-1">
                                                <ParentListItem
                                                    parent={ parent }
                                                    condensed
                                                    actions={(
                                                        <ButtonGroup>
                                                            <Button tag={Link} to={ `/users/show/${ parent?.id }` } color="warning" size="sm">
                                                                <Icon path={mdiAccountArrowRightOutline} size={1} />
                                                            </Button>&nbsp;
                                                            <Button onClick={() => removeParent(parent)} color="danger" size="sm">
                                                                <Icon path={mdiTrashCanOutline} size={1} />
                                                            </Button>&nbsp;
                                                        </ButtonGroup>
                                                    )}
                                                />
                                            </Col>
                                        ))}
                                        </Row>
                                    </CardText>
                                    )}
                                    {student?.siblings?.length > 0 && (
                                    <CardText tag="div" className="mt-4">
                                        <CardTitle tag="h4">
                                            { t('students.siblings') }
                                        </CardTitle>
                                        <Row className="row-cols-sm-1 row-cols-lg-1 row-cols-xl-1">
                                        { student?.siblings?.map(student => (
                                            <Col sm="12" md="12" className="mb-1">
                                                <StudentListItem
                                                    student={ student }
                                                    condensed
                                                    actions={(
                                                        <Button tag={Link} to={ `/students/edit/${ student?.id }` } color="warning" size="sm">
                                                            <Icon path={mdiAccountArrowRightOutline} size={1} />
                                                        </Button>
                                                    )}
                                                />
                                            </Col>
                                        ))}
                                        </Row>
                                    </CardText>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        )}
                    </Row>
                    { !isCreation && (
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountGroupOutline} size={1.5} />&nbsp;
                                        {t('students.group')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <Card color="primary" className="text-light">
                                        <CardBody>
                                            <Row className="mb-0">
                                                <Col className="col-auto">
                                                    <CardTitle tag="h4">
                                                        { t('students.set_group') }
                                                    </CardTitle>
                                                </Col>
                                                <Col>
                                                    <CardText>
                                                        <GroupsField single name="new_group" groups={ groups } className="w-100" />
                                                    </CardText>
                                                </Col>
                                                <Col className="col-auto">
                                                    <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                                        <span>
                                                            <Icon path={isLoading ? mdiLoading : mdiAccountMultiplePlus} spin={isLoading ? 1 : 0} size={1} />&nbsp;
                                                            {t("common.assign")}
                                                        </span>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                    <CardText tag="div" className="mt-4">
                                        <CardTitle tag="h3">
                                            { t('students.actual_group') }
                                        </CardTitle>
                                        <CardTitle tag="h5">
                                            { student?.group?.id && (
                                                <span>
                                                    { student?.group.name } -&nbsp;
                                                    <small>{student?.group.getAgesText()}</small>
                                                </span>
                                            )}
                                            { !student?.group?.id && t('students.has_no_group') }
                                        </CardTitle>
                                        { student?.group && student?.group.classrooms?.map( classroom => (
                                            <ClassroomListItem classroom={ classroom } />
                                        ))}
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardHeader>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiAccountCreditCardOutline} size={1.5} />&nbsp;
                                        {t('students.enrolment_fees')}
                                    </CardTitle>
                                </CardHeader>
                                <CardBody>
                                    <CardText tag="div">
                                        <CardTitle tag="h3">
                                            { t('students.invoices') }
                                        </CardTitle>
                                        <CardTitle tag="h5">
                                            { !student?.invoices?.length && t('students.has_no_invoice') }
                                        </CardTitle>
                                        { student?.invoices?.map((invoice, i) => (
                                            <InvoiceDetails
                                                paymentName="new_payment"
                                                invoice={invoice}
                                                className="h-100"
                                                pristine={pristine}
                                                submitting={submitting}
                                                isLoadingOne={isLoadingOne}
                                                onPaymentDeleted={reloadStudent}
                                            />
                                        ))}
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    )}
                    <Row className="main row-flex">
                        <Col sm={12} lg={6}>
                            <Card color="light" className="h-100">
                                <CardBody>
                                    <CardTitle tag="h3">
                                        <Icon path={mdiCommentAccountOutline} size={1.5} />&nbsp;
                                        {t('students.other_informations')}
                                    </CardTitle>
                                    <CardText tag="div">
                                    </CardText>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    <Row className="main">
                        <Col className="text-center">
                            <ButtonGroup>
                                <Button tag={Link} to={ -1 } color="warning">
                                    <span><i className="fa fa-arrow-left"></i>&nbsp;{t('common.back')}</span>
                                </Button>
                                <Button type="submit" color="success" disabled={pristine || submitting || isLoading}>
                                    <span>
                                        <i className={`fa ${ isLoading ? 'fa-spinner fa-spin' : 'fa fa-save'}`}></i>&nbsp;{t("common.save")}
                                    </span>
                                </Button>
                            </ButtonGroup>
                        </Col>
                    </Row>
                </form>
        )} />
    );
}

export default StudentForm;
